import {
    Button,
    Card,
    CardBody,
    Flex,
    Grid,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Portal,
    Stack,
    Text,
} from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { snakeCase } from 'lodash';
import { useMemo, useState } from 'react';
import { BiMenu, BiMinus, BiPlus } from 'react-icons/bi';
import { RiBook2Line } from 'react-icons/ri';
import { useSearchParams } from 'react-router-dom';

import { PortfolioDrawer, usePortfolioDrawerControls } from '@/cip/Portfolio/PortfolioDrawer/PortfolioDrawer';

import { GrowthAllocationType, Portfolio, PortfolioType, ResourceType } from '../_openapi/portfolio-optimizer';
import { usePreTradeAllocationReport } from '../api/allocation';
import { usePortfolios } from '../api/portfolio';
import { ManualTrade } from '../TradeBlotter/TradeBlotter';
import { useTradeBlotterContext } from '../TradeBlotter/TradeBlotterContext';
import { tradeCalculator } from '../TradeBlotter/tradeCalculator';
import { DataVariantSelector } from '../widgets/DataVariantSelector';
import { MultiSelectFilter } from '../widgets/filters/MultiSelectFilter';
import { PageContent } from '../widgets/PageContent';
import { PageHeader } from '../widgets/PageHeader';
import { AsOfDateTag } from '../widgets/Table/AsOfDateTag';
import { CreateWidgetFromTable } from '../widgets/Table/CreateWidgetFromTable';
import { NumberCell } from '../widgets/Table/NumberCell';
import { Pagination } from '../widgets/Table/Pagination';
import { Table } from '../widgets/Table/Table';
import { TableControls } from '../widgets/Table/TableControls';
import { usePersistentTableState } from '../widgets/Table/usePersistentTableState';
import { useTableColumnSettings } from '../widgets/Table/useTableColumnSettings';
import { portfolioListColumns } from './portfolioListColumns';

const columnHelper = createColumnHelper<Portfolio>();

export const PortfolioList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { viewPortfolio, ...portfolioDrawerProps } = usePortfolioDrawerControls();
    const { pagination, setPagination, sorting, setSorting, resetTable, columnFilters, setColumnFilters } =
        usePersistentTableState(portfolioListColumns);
    const { addTrade } = useTradeBlotterContext();
    const [selectedGrowthAllocationTypes, setSelectedGrowthAllocationTypes] = useState<GrowthAllocationType[]>([]);
    const isGrowthAllocationFilterActive = selectedGrowthAllocationTypes.length > 0;

    const growthAllocationColumnFilters = useMemo(
        () =>
            isGrowthAllocationFilterActive
                ? [
                      {
                          id: 'allocationType',
                          value: selectedGrowthAllocationTypes,
                      },
                  ]
                : [],
        [selectedGrowthAllocationTypes],
    );
    const { data: growthAllocationPage, isFetching: isFetchingGrowthAllocations } = usePreTradeAllocationReport({
        // Intentionally omit pagination: this endpoint returns all matching rows when page params are absent.
        columnFilters: growthAllocationColumnFilters,
        enabled: isGrowthAllocationFilterActive,
    });
    const growthAllocationPortfolioIds = useMemo(
        () => Array.from(new Set(growthAllocationPage?.growthAllocation.map(({ pId }) => pId) ?? [])),
        [growthAllocationPage],
    );
    const isResolvingGrowthAllocations = isGrowthAllocationFilterActive && isFetchingGrowthAllocations;
    const hasResolvedGrowthAllocations =
        isGrowthAllocationFilterActive && !isFetchingGrowthAllocations && !!growthAllocationPage;
    const hasGrowthAllocationMatches = hasResolvedGrowthAllocations && growthAllocationPortfolioIds.length > 0;
    const hasNoGrowthAllocationMatches = hasResolvedGrowthAllocations && !hasGrowthAllocationMatches;
    const portfolioColumnFilters = useMemo(
        () =>
            isGrowthAllocationFilterActive
                ? [
                      ...columnFilters,
                      {
                          id: 'pId',
                          value: growthAllocationPortfolioIds,
                      },
                  ]
                : columnFilters,
        [columnFilters, growthAllocationPortfolioIds, isGrowthAllocationFilterActive],
    );

    const isPortfolioQueryEnabled = !isGrowthAllocationFilterActive || hasGrowthAllocationMatches;

    const dataVariant = (searchParams.get('dataVariant') as DataVariant) || 'report';
    const {
        data: portfolioPage,
        isLoading,
        isFetching: isFetchingPortfolios,
    } = usePortfolios({
        dataVariant,
        pagination,
        columnFilters: portfolioColumnFilters,
        sorting,
        enabled: isPortfolioQueryEnabled,
    });
    const displayedPortfolioPage = hasNoGrowthAllocationMatches
        ? {
              ...portfolioPage,
              portfolios: [],
              totalNr: 0,
          }
        : portfolioPage;
    const columns = useMemo(() => {
        return [
            columnHelper.display({
                id: 'contextMenu',
                cell: ({ row }) => {
                    return (
                        <Menu isLazy>
                            <MenuButton
                                size="xs"
                                as={IconButton}
                                aria-label="Options"
                                icon={<Icon boxSize={3} as={BiMenu} />}
                                w="20px"
                                minW="20px"
                                h="20px"
                                variant="outline"
                                borderRadius="sm"
                            />
                            <Portal>
                                <MenuList>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={RiBook2Line} />}
                                        onClick={() => viewPortfolio(row.original.base.pId)}
                                    >
                                        Portfolio details
                                    </MenuItem>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={BiMinus} />}
                                        onClick={() => {
                                            addTrade({
                                                portfolio: {
                                                    portfolioGuid: row.original.base.portfolioGuid,
                                                },
                                                side: 'SELL',
                                                quantity: 0,
                                                orderType: 'LMT',
                                                price: {
                                                    type: tradeCalculator.getPriceTypeFromTradeSide('SELL'),
                                                },
                                            } as ManualTrade);
                                        }}
                                    >
                                        Add Sell on Portfolio
                                    </MenuItem>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={BiPlus} />}
                                        onClick={() => {
                                            addTrade({
                                                portfolio: {
                                                    portfolioGuid: row.original.base.portfolioGuid,
                                                },
                                                side: 'BUY',
                                                quantity: 0,
                                                orderType: 'LMT',
                                                price: {
                                                    type: tradeCalculator.getPriceTypeFromTradeSide('BUY'),
                                                },
                                            } as ManualTrade);
                                        }}
                                    >
                                        Add Buy on Portfolio
                                    </MenuItem>
                                </MenuList>
                            </Portal>
                        </Menu>
                    );
                },
                enableSorting: false,
                meta: {
                    isDisplayCol: true,
                    isSticky: true,
                },
            }),
            ...portfolioListColumns,
        ];
    }, [addTrade, viewPortfolio]);

    const { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder } = useTableColumnSettings('portfolios');

    const table = useReactTable({
        data: displayedPortfolioPage?.portfolios || [],
        columns,
        state: {
            columnVisibility,
            columnOrder,
            pagination,
            sorting,
            columnFilters,
        },
        initialState: {
            columnPinning: {
                left: ['contextMenu'],
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
            <PageHeader title="Portfolios">
                <Flex flex={1} justifyContent="space-between" alignItems="center">
                    <DataVariantSelector
                        currentDataVariant={dataVariant}
                        onClick={(variant) => {
                            setSearchParams((params) => {
                                params.set('dataVariant', variant);
                                return params;
                            });
                        }}
                    />
                    <AsOfDateTag dateString={displayedPortfolioPage?.asOfDate} />
                </Flex>
            </PageHeader>
            <PageContent isLoading={isLoading}>
                <Stack gap={4}>
                    <PortfolioStats totalNr={displayedPortfolioPage?.totalNr} />

                    <Card>
                        <CardBody>
                            <TableControls
                                table={table}
                                onReset={() => {
                                    setSelectedGrowthAllocationTypes([]);
                                    resetTable();
                                }}
                                resourceType={ResourceType.PORTFOLIO}
                                metaData={{ portfolioType: snakeCase(dataVariant) as PortfolioType }}
                                exportColumnFilters={portfolioColumnFilters}
                            >
                                <Flex ml="auto" gap={2}>
                                    <GrowthAllocationTypeFilter
                                        value={selectedGrowthAllocationTypes}
                                        isLoading={isResolvingGrowthAllocations}
                                        onChange={(value) => {
                                            setSelectedGrowthAllocationTypes(value);
                                            setPagination((current) => ({ ...current, pageIndex: 0 }));
                                        }}
                                    />
                                    <CreateWidgetFromTable
                                        initialValue={{
                                            type: 'portfolio',
                                            queryMeta: {
                                                dataVariant,
                                            },
                                            filters: table.getState().columnFilters,
                                            sort: table.getState().sorting,
                                        }}
                                    />
                                </Flex>
                            </TableControls>
                            <Table
                                table={table}
                                isLoading={isFetchingPortfolios || isResolvingGrowthAllocations}
                                allowXOverflow
                            />
                            <Pagination table={table} totalItems={displayedPortfolioPage?.totalNr} />
                        </CardBody>
                    </Card>
                </Stack>
            </PageContent>
            <PortfolioDrawer {...portfolioDrawerProps} />
        </>
    );
};

const GrowthAllocationTypeFilter = ({
    value,
    onChange,
    isLoading,
}: {
    value: GrowthAllocationType[];
    onChange: (value: GrowthAllocationType[]) => void;
    isLoading: boolean;
}) => {
    return (
        <Menu closeOnSelect={false} isLazy>
            <MenuButton
                as={Button}
                size="sm"
                colorScheme={value.length ? 'teal' : undefined}
                variant={value.length ? 'solid' : 'outline'}
                isLoading={isLoading}
            >
                Growth Allocation{value.length ? ` (${value.length})` : ''}
            </MenuButton>
            <MenuList minW="260px" maxH="420px" overflowY="auto" p={3}>
                <MultiSelectFilter
                    options={Object.values(GrowthAllocationType)}
                    filterValue={value}
                    setFilterValue={(filterValue) => onChange((filterValue ?? []) as GrowthAllocationType[])}
                    allowClear
                />
            </MenuList>
        </Menu>
    );
};

const PortfolioStats = ({ totalNr }: { totalNr?: number }) => {
    return (
        <Card>
            <CardBody>
                <Grid templateColumns="max-content max-content" columnGap={6}>
                    <Text>Number of Portfolios</Text>
                    <NumberCell value={totalNr} fractionDigits={0} />
                </Grid>
            </CardBody>
        </Card>
    );
};
